'use client';

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { MARQ_EMPTY_EVENT } from '@/lib/api';
import MarqCoin from './MarqCoin';
import RewardCard from './rewards/RewardCard';
import { openGuestPrompt } from './GuestPrompt';
import { NAV_BTN } from './nav/styles';

/**
 * The KHARMA chip on the navbar (coin and balance), and the daily reward
 * panel behind it.
 *
 * ── Why the reward is a dot on the balance, and not its own button ─────────
 * The balance and the reward are one thing to the person: what they hold, and
 * the free top-up of it. A labelled "Claim" pill would read as the primary
 * action of the whole bar, every day, for a thing worth one press. The dot is
 * the smallest mark that survives peripheral vision, and it disappears the
 * moment the reward is taken.
 *
 * ── A guest sees the gift, but cannot open it ───────────────────────────────
 * The reward is for accounts (KHARMA is an account wallet — lib/marq.ts). A guest
 * has no balance and nothing to claim, but the gift still shows for them as a
 * HOOK: clicking it does not open the panel, it opens the sign-up invitation.
 * With no account at all behind the page (KHARMA off, or guest mode off) there is
 * nothing to offer and the button renders nothing.
 */
export default function DailyReward() {
  const { marq, user, guest } = useAuth();
  // A signed-out visitor who is allowed to use the translator — the gift is
  // their nudge toward an account, not a wallet they own.
  const isGuest = !user && !!guest && guest.total > 0;
  const [open, setOpen] = useState(false);
  /** Pulses when a spend was refused for want of KHARMA — see the effect below. */
  const [urgent, setUrgent] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const canClaim = !!marq?.canClaimDaily;

  /*
   * A refused spend turns the gift urgent.
   *
   * This is the one moment the reward is genuinely the answer to the person's
   * problem, and the error line in the output pane cannot point at a control
   * two inches away. lib/api.ts raises the event from both the streaming and
   * the JSON paths, so every spend in the product arrives here.
   */
  useEffect(() => {
    const onEmpty = () => {
      setUrgent(true);
      setOpen(true);
    };
    window.addEventListener(MARQ_EMPTY_EVENT, onEmpty);
    return () => window.removeEventListener(MARQ_EMPTY_EVENT, onEmpty);
  }, []);

  // Close on an outside click or Escape — the same contract every other popover
  // in this header honours, so the row behaves as one thing.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); wrapRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);


  // A guest: the gift is a hook to an account, not a wallet. One tap opens the
  // sign-up invitation — no panel, no balance, nothing to claim without one.
  if (!marq) {
    if (!isGuest) return null;
    return (
      <button
        type="button"
        onClick={() => openGuestPrompt('reward')}
        aria-label="Daily reward — sign up to collect it"
        title="Daily reward — sign up to collect it"
        className={NAV_BTN}
      >
        <GiftGlyph />
      </button>
    );
  }

  return (
    <div ref={wrapRef} className="relative shrink-0">
      {/*
        The wallet on the navbar: the coin and the balance, one chip. Behind it
        is the daily reward, so the amber dot on the chip is the only mark the
        reward ever needs. The number is the first thing the bar gives up when
        it runs short of room (components/nav/SiteNav.tsx); the coin stays.
      */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`${marq.balance.toLocaleString('en-US')} KHARMA${canClaim ? ` — daily reward of ${marq.dailyAmount} waiting` : ''}`}
        aria-expanded={open}
        title={canClaim ? `${marq.balance.toLocaleString('en-US')} KHARMA · claim ${marq.dailyAmount} more today` : `${marq.balance.toLocaleString('en-US')} KHARMA · your credit for AI requests and games`}
        className={
          'relative flex h-9 items-center gap-1.5 rounded-full px-2 text-[13px] font-semibold transition ' +
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 ' +
          (marq.balance <= 0
            ? 'text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40'
            : 'text-ink-800 hover:bg-ink-100 dark:text-ink-100 dark:hover:bg-white/[0.08]') +
          (open ? ' bg-ink-100 dark:bg-white/[0.08]' : '')
        }
      >
        <MarqCoin className="h-[22px] w-[22px]" />
        <span data-fit="balance" className="pr-0.5 tabular-nums">{marq.balance.toLocaleString('en-US')}</span>

        {/*
          The dot. `pointer-events-none` because it overhangs the button and
          must not be what swallows the click. It pulses only after a refused
          spend, so the everyday state stays a quiet mark.
        */}
        {canClaim && (
          <span
            className={
              'pointer-events-none absolute left-[22px] top-1 h-2.5 w-2.5 rounded-full ' +
              'bg-amber-500 ring-2 ring-white dark:ring-ink-900 ' +
              (urgent ? 'gt-marq-pulse' : '')
            }
            aria-hidden
          />
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Your KHARMA rewards" className="fixed right-3 top-16 z-[110] max-h-[calc(100dvh-80px)] w-[420px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-[18px] border border-ink-200 bg-white shadow-2xl dark:border-ink-700 dark:bg-ink-900">
          <div className="flex justify-end px-4 pt-2"><button type="button" aria-label="Close rewards" className="rounded-lg px-2 py-1 text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800" onClick={() => setOpen(false)}>×</button></div>
          <RewardCard compact />
        </div>
      )}
    </div>
  );
}

/**
 * The gift box — same 1.7 stroke as the sun, moon, gamepad and gear beside it,
 * so the account's button and the guest's hook are the same mark. A filled
 * glyph in this row would read as the pressed state of a button that has none.
 */
function GiftGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="9" width="18" height="12" rx="1.5" />
      <path d="M3 13h18M12 9v12" />
      <path d="M12 9C12 9 10.5 3 7.5 3A2.5 2.5 0 0 0 7.5 9zM12 9c0 0 1.5-6 4.5-6a2.5 2.5 0 0 1 0 6z" />
    </svg>
  );
}
